import PropTypes from 'prop-types';
import { useTranslations } from 'next-intl';
import CheckboxInput from '@/components/form-fields/CheckboxInput';

const CategorySideBar = ({
  subcategories,
  onCategorySelect,
  activeCategoryId,
  onFilterChange,
  activeFilters,
  loadingCategoryId,
  tags,
  tagsLoading,
  tagsError,
}) => {
  const t = useTranslations('Product');

  // Handle the selection of a tag
  const handleTagSelect = (tagId) => {
    const currentTagIds = [...activeFilters.tagIds];
    const tagIndex = currentTagIds.indexOf(tagId);

    if (tagIndex === -1) {
      // Add the tag
      currentTagIds.push(tagId);
    } else {
      // Remove the tag
      currentTagIds.splice(tagIndex, 1);
    }

    onFilterChange({
      ...activeFilters,
      tagIds: currentTagIds,
    });
  };

  // Handle the "on sale only" toggle
  const updateFilter = (key, value) => {
    if (!onFilterChange || !activeFilters) return;
    onFilterChange({
      ...activeFilters,
      [key]: value,
    });
  };

  const handleSaleToggle = () => {
    updateFilter('onSaleOnly', !activeFilters.onSaleOnly);
  };

  return (
    <div className="sidebar-wrapper">
      <div className="subcategories-nav mb-4">
        <ul>
          {subcategories.map((subcategory) => (
            <li key={subcategory.id}>
              <button
                className={`p-2 w-full text-xl text-primary-studio font-secondary hover:bg-gray-300 text-start flex items-center ${activeCategoryId === String(subcategory.id) ? 'bg-gray-300' : ''}`}
                onClick={() => onCategorySelect(subcategory.id)}
                disabled={loadingCategoryId !== null}
              >
                {loadingCategoryId === subcategory.id && (
                  <span className="me-1 animate-spin rounded-full border-b-2 border-gray-900 h-4 w-4"></span>
                )}
                {subcategory.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="filters mb-4">
        <h3 className="text-2xl font-semibold mb-2 text-primary-studio font-secondary">{t('filters')}</h3>
        <hr className="dotted-spaced my-4" />

        {Array.isArray(tags) && tags.length > 0 && !tagsLoading && (
          <ul className="max-h-60 overflow-y-auto">
            <li>
              <CheckboxInput
                name="onSaleOnly"
                value={activeFilters?.onSaleOnly || false}
                checked={activeFilters?.onSaleOnly || false}
                onChange={handleSaleToggle}
                label={t('is_on_sale')}
                className="text-primary-studio h-4 w-4 checked:bg-blue-500"
                labelClassName="text-primary-studio text-lg font-secondary"
              />
            </li>
            {tags.map((tag) => (
              <li key={tag.id}>
                <CheckboxInput
                  name={`tag-${tag.id}`}
                  value={activeFilters?.tagIds.includes(tag.id)}
                  checked={activeFilters?.tagIds.includes(tag.id)}
                  onChange={() => handleTagSelect(tag.id)}
                  label={tag.name}
                  className="text-primary-studio h-4 w-4 checked:bg-blue-500"
                  labelClassName="text-primary-studio text-lg font-secondary"
                />
              </li>
            ))}
          </ul>
        )}

        {tagsLoading && <p className="text-sm text-gray-500 mt-2">{t('loading')}</p>}
        {tagsError && !tagsLoading && <p className="text-sm text-red-500 mt-2">{t('failed_to_load_tags')}</p>}
        {!tagsLoading && !tagsError && (!Array.isArray(tags) || tags.length === 0) &&
          <p className="text-sm text-gray-500 mt-2">{t('no_tags_found')}</p>
        }
      </div>
    </div>
  );
};

CategorySideBar.propTypes = {
  subcategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onCategorySelect: PropTypes.func.isRequired,
  activeCategoryId: PropTypes.string,
  onFilterChange: PropTypes.func.isRequired,
  activeFilters: PropTypes.shape({
    tagIds: PropTypes.array.isRequired,
    onSaleOnly: PropTypes.bool.isRequired,
  }).isRequired,
  loadingCategoryId: PropTypes.number,
  tags: PropTypes.array,
  tagsLoading: PropTypes.bool,
  tagsError: PropTypes.object,
};

export default CategorySideBar;